import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import cacheKeys from "@/config/cache-keys";
import AppService from "@/services/AppService";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";
import { productDefinitionsToTemplates } from "@/types/insurance/productDefinition";

const service = new AppService();

/**
 * Fetches the insurance product catalog from the backend and returns it as
 * an ordered list of `CreateInsuranceFormData` templates ready to seed the
 * create-account form.
 *
 * The product catalog rarely changes, so we cache it aggressively: a long
 * `staleTime` avoids refetching during a session, but it remains a normal
 * react-query entry that can be invalidated explicitly if needed.
 */
export const useProductDefinitions = () => {
	const query = useQuery({
		queryKey: cacheKeys.INSURANCE_PRODUCT_DEFINITIONS,
		queryFn: async () => {
			const response = await service.getInsuranceProductDefinitions();
			return response.Result ?? [];
		},
		staleTime: 1000 * 60 * 60, // 1 hour
		gcTime: 1000 * 60 * 60 * 24, // 24 hours
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const templates = useMemo<CreateInsuranceFormData[]>(
		() => (query.data ? productDefinitionsToTemplates(query.data) : []),
		[query.data],
	);

	return {
		templates,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
};
